import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft,
  Save,
  Mail,
  Image,
  FileText,
  Settings,
  Inbox,
  Loader2,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  RefreshCw,
  Plus,
  Home,
  Grid,
  HelpCircle,
  Lock,
  LogOut,
  QrCode,
  Smartphone,
  BarChart3,
  ExternalLink,
  Search,
  RotateCcw,
  ImageIcon
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Login Component
const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${BACKEND_URL}/api/admin/login`, { password });
      // Store login state
      sessionStorage.setItem('adminLoggedIn', 'true');
      onLogin();
    } catch (err) {
      setError('Napačno geslo. Poskusite znova.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
      <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#2D4A5E] rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[#2D4A5E]">Admin Panel</h1>
          <p className="text-gray-500 mt-2">Vnesite geslo za dostop</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Geslo</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoFocus
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent outline-none"
              placeholder="Vnesite geslo"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#4CAF50] hover:bg-[#45a049] text-white font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Preverjanje...
              </>
            ) : (
              'Prijava'
            )}
          </button>
        </form>

        <p className="text-center text-gray-400 text-xs mt-6">
          Dostop je zaščiten z geslom.
        </p>
      </div>
    </div>
  );
};

const AdminPanel = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeTab, setActiveTab] = useState('inquiries');
  const [inquiries, setInquiries] = useState([]);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    // Check if already logged in
    const loggedIn = sessionStorage.getItem('adminLoggedIn');
    if (loggedIn === 'true') {
      setIsLoggedIn(true);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      fetchData();
    }
  }, [isLoggedIn]);

  const handleLogout = () => {
    sessionStorage.removeItem('adminLoggedIn');
    setIsLoggedIn(false);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [inquiriesRes, contentRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/inquiries`),
        axios.get(`${BACKEND_URL}/api/content`)
      ]);
      setInquiries(inquiriesRes.data);
      setContent(contentRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleContentChange = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  const saveContent = async () => {
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/content`, content);
      setMessage({ type: 'success', text: 'Vsebina uspešno shranjena!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Napaka pri shranjevanju.' });
    } finally {
      setSaving(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/inquiries/${id}`, { read: true });
      setInquiries(prev => prev.map(inq => inq.id === id ? { ...inq, read: true } : inq));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm('Ali ste prepričani, da želite izbrisati to povpraševanje?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/inquiries/${id}`);
      setInquiries(prev => prev.filter(inq => inq.id !== id));
    } catch (error) {
      console.error('Error deleting inquiry:', error);
    }
  };

  // Show login if not logged in
  if (!isLoggedIn) {
    return <AdminLogin onLogin={() => setIsLoggedIn(true)} />;
  }

  const tabs = [
    { id: 'inquiries', label: 'Povpraševanja', icon: Inbox },
    { id: 'hero', label: 'Hero', icon: Home },
    { id: 'services', label: 'Storitve', icon: Grid },
    { id: 'gallery', label: 'Galerija', icon: Image },
    { id: 'why', label: 'Zakaj mi', icon: HelpCircle },
    { id: 'about', label: 'O nas', icon: FileText },
    { id: 'contact', label: 'Kontakt', icon: Settings },
    { id: 'landing', label: 'QR/NFC', icon: QrCode },
    { id: 'seo', label: 'SEO', icon: Search },
    { id: 'images', label: 'Slike', icon: ImageIcon }
  ];

  const unreadCount = inquiries.filter(i => !i.read).length;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#2D4A5E] text-white py-4 px-6 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft size={20} />
              <span>Nazaj na stran</span>
            </button>
          </div>
          <h1 className="text-xl font-bold">Admin Panel</h1>
          <div className="flex items-center gap-4">
            <button
              onClick={fetchData}
              className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
            >
              <RefreshCw size={20} />
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
              title="Odjava"
            >
              <LogOut size={20} />
              <span className="hidden sm:inline">Odjava</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto py-8 px-6">
        {/* Message */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {message.type === 'success' ? <CheckCircle size={20} /> : <XCircle size={20} />}
            {message.text}
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-t-xl shadow-sm overflow-x-auto">
          <div className="flex border-b min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-4 font-medium transition-colors relative whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-[#4CAF50] border-b-2 border-[#4CAF50]'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon size={18} />
                {tab.label}
                {tab.id === 'inquiries' && unreadCount > 0 && (
                  <span className="ml-1 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {unreadCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-b-xl shadow-sm p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#4CAF50]" />
            </div>
          ) : (
            <>
              {activeTab === 'inquiries' && (
                <InquiriesTab inquiries={inquiries} markAsRead={markAsRead} deleteInquiry={deleteInquiry} />
              )}
              {activeTab === 'hero' && content && (
                <HeroTab content={content} onChange={handleContentChange} onSave={saveContent} saving={saving} />
              )}
              {activeTab === 'services' && content && (
                <ServicesTab content={content} onChange={handleContentChange} onSave={saveContent} saving={saving} />
              )}
              {activeTab === 'gallery' && content && (
                <GalleryTab content={content} onChange={handleContentChange} onSave={saveContent} saving={saving} />
              )}
              {activeTab === 'why' && content && (
                <WhyTab content={content} onChange={handleContentChange} onSave={saveContent} saving={saving} />
              )}
              {activeTab === 'about' && content && (
                <AboutTab content={content} onChange={handleContentChange} onSave={saveContent} saving={saving} />
              )}
              {activeTab === 'contact' && content && (
                <ContactTab content={content} onChange={handleContentChange} onSave={saveContent} saving={saving} />
              )}
              {activeTab === 'landing' && content && (
                <LandingTab content={content} onChange={handleContentChange} onSave={saveContent} saving={saving} />
              )}
              {activeTab === 'seo' && content && (
                <SeoTab content={content} onChange={handleContentChange} onSave={saveContent} saving={saving} />
              )}
              {activeTab === 'images' && content && (
                <ImagesTab content={content} onChange={handleContentChange} onSave={saveContent} saving={saving} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};


// ============== TAB COMPONENTS ==============

// Inquiries Tab
const InquiriesTab = ({ inquiries, markAsRead, deleteInquiry }) => {
  const [selectedInquiry, setSelectedInquiry] = useState(null);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('sl-SI', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  if (inquiries.length === 0) {
    return (
      <div className="text-center py-12">
        <Inbox className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-500">Ni povpraševanj</h3>
        <p className="text-gray-400">Ko prejmete povpraševanje, se bo pojavilo tukaj.</p>
      </div>
    );
  }

  if (selectedInquiry) {
    return (
      <div>
        <button onClick={() => setSelectedInquiry(null)} className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeft size={20} /> Nazaj na seznam
        </button>
        <div className="bg-gray-50 rounded-lg p-6">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-xl font-bold text-[#2D4A5E]">{selectedInquiry.first_name} {selectedInquiry.last_name}</h3>
            <span className="text-sm text-gray-500">{formatDate(selectedInquiry.created_at)}</span>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div><span className="text-sm text-gray-500">Email:</span><a href={`mailto:${selectedInquiry.email}`} className="block text-[#4CAF50] font-medium">{selectedInquiry.email}</a></div>
            <div><span className="text-sm text-gray-500">Telefon:</span><a href={`tel:${selectedInquiry.phone}`} className="block text-[#4CAF50] font-medium">{selectedInquiry.phone || '-'}</a></div>
            <div><span className="text-sm text-gray-500">Storitev:</span><p className="font-medium">{selectedInquiry.service}</p></div>
          </div>
          <div><span className="text-sm text-gray-500">Sporočilo:</span><p className="mt-2 p-4 bg-white rounded-lg border">{selectedInquiry.message}</p></div>
          <div className="mt-4">
            <a href={`mailto:${selectedInquiry.email}?subject=Odgovor na vaše povpraševanje - ANZO`} className="flex items-center gap-2 bg-[#4CAF50] text-white px-4 py-2 rounded-lg hover:bg-[#45a049] inline-flex">
              <Mail size={18} /> Odgovori
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b">
            <th className="text-left py-3 px-4 font-medium text-gray-500">Ime</th>
            <th className="text-left py-3 px-4 font-medium text-gray-500">Email</th>
            <th className="text-left py-3 px-4 font-medium text-gray-500">Storitev</th>
            <th className="text-left py-3 px-4 font-medium text-gray-500">Datum</th>
            <th className="text-right py-3 px-4 font-medium text-gray-500">Akcije</th>
          </tr>
        </thead>
        <tbody>
          {inquiries.map((inquiry) => (
            <tr key={inquiry.id} className={`border-b hover:bg-gray-50 cursor-pointer ${!inquiry.read ? 'bg-blue-50' : ''}`}
              onClick={() => { setSelectedInquiry(inquiry); if (!inquiry.read) markAsRead(inquiry.id); }}>
              <td className="py-3 px-4">
                <div className="flex items-center gap-2">
                  {!inquiry.read && <div className="w-2 h-2 bg-blue-500 rounded-full"></div>}
                  <span className={!inquiry.read ? 'font-semibold' : ''}>{inquiry.first_name} {inquiry.last_name}</span>
                </div>
              </td>
              <td className="py-3 px-4 text-gray-600">{inquiry.email}</td>
              <td className="py-3 px-4 text-gray-600">{inquiry.service}</td>
              <td className="py-3 px-4 text-gray-600">{new Date(inquiry.created_at).toLocaleDateString('sl-SI')}</td>
              <td className="py-3 px-4 text-right">
                <button onClick={(e) => { e.stopPropagation(); deleteInquiry(inquiry.id); }} className="text-gray-400 hover:text-red-500 p-1">
                  <Trash2 size={18} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Save Button Component
const SaveButton = ({ onSave, saving }) => (
  <button onClick={onSave} disabled={saving} className="flex items-center gap-2 bg-[#4CAF50] text-white px-6 py-3 rounded-lg hover:bg-[#45a049] disabled:opacity-50">
    {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save size={20} />}
    Shrani spremembe
  </button>
);

// Hero Tab
const HeroTab = ({ content, onChange, onSave, saving }) => (
  <div className="space-y-6">
    <h3 className="text-lg font-bold text-[#2D4A5E]">Hero sekcija (uvodni del)</h3>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Naslov</label>
        <input type="text" value={content.hero_title || ''} onChange={(e) => onChange('hero_title', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Podnaslov</label>
        <input type="text" value={content.hero_subtitle || ''} onChange={(e) => onChange('hero_subtitle', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
      </div>
      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-2">Opis</label>
        <textarea value={content.hero_description || ''} onChange={(e) => onChange('hero_description', e.target.value)} rows={4}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Gumb - besedilo</label>
        <input type="text" value={content.hero_button_text || ''} onChange={(e) => onChange('hero_button_text', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Slika ozadja (URL)</label>
        <input type="url" value={content.hero_background_image || ''} onChange={(e) => onChange('hero_background_image', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
      </div>
    </div>
    
    {content.hero_background_image && (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Predogled slike ozadja</label>
        <img src={content.hero_background_image} alt="Hero ozadje" className="w-full max-w-md h-48 object-cover rounded-lg border" />
      </div>
    )}
    
    <div className="pt-6 border-t"><SaveButton onSave={onSave} saving={saving} /></div>
  </div>
);

// Services Tab
const ServicesTab = ({ content, onChange, onSave, saving }) => {
  const services = content.services || [];
  const [expandedService, setExpandedService] = useState(null);
  
  const updateService = (index, field, value) => {
    const updated = [...services];
    updated[index] = { ...updated[index], [field]: value };
    onChange('services', updated);
  };
  
  const addService = () => {
    onChange('services', [...services, { id: Date.now().toString(), title: 'Nova storitev', description: '', full_description: '', image: '', gallery_images: [] }]);
  };
  
  const removeService = (index) => {
    if (window.confirm('Ali ste prepričani?')) {
      onChange('services', services.filter((_, i) => i !== index));
    }
  };

  const addGalleryImage = (serviceIndex) => {
    const url = prompt('Vnesite URL slike:');
    if (url && url.trim()) {
      const currentGallery = services[serviceIndex].gallery_images || [];
      updateService(serviceIndex, 'gallery_images', [...currentGallery, url.trim()]);
    }
  };

  const removeGalleryImage = (serviceIndex, imageIndex) => {
    const currentGallery = services[serviceIndex].gallery_images || [];
    updateService(serviceIndex, 'gallery_images', currentGallery.filter((_, i) => i !== imageIndex));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-bold text-[#2D4A5E]">Storitve</h3>
        <button onClick={addService} className="flex items-center gap-2 bg-[#4CAF50] text-white px-4 py-2 rounded-lg hover:bg-[#45a049]">
          <Plus size={18} /> Dodaj storitev
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Naslov sekcije</label>
          <input type="text" value={content.services_title || ''} onChange={(e) => onChange('services_title', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Podnaslov sekcije</label>
          <input type="text" value={content.services_subtitle || ''} onChange={(e) => onChange('services_subtitle', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
        </div>
      </div>
      
      <div className="space-y-4">
        {services.map((service, index) => (
          <div key={service.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div 
              className="flex justify-between items-center cursor-pointer"
              onClick={() => setExpandedService(expandedService === index ? null : index)}
            >
              <div className="flex items-center gap-3">
                {service.image && <img src={service.image} alt={service.title} className="w-12 h-12 object-cover rounded-lg" />}
                <div>
                  <h4 className="font-semibold text-[#2D4A5E]">{service.title || 'Nova storitev'}</h4>
                  <p className="text-sm text-gray-500">{(service.gallery_images || []).length} slik v galeriji</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={(e) => { e.stopPropagation(); removeService(index); }} className="text-red-500 hover:text-red-700 p-1"><Trash2 size={18} /></button>
                <span className="text-gray-400">{expandedService === index ? '▼' : '▶'}</span>
              </div>
            </div>
            
            {expandedService === index && (
              <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Naziv storitve</label>
                    <input type="text" value={service.title} onChange={(e) => updateService(index, 'title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Glavna slika (URL)</label>
                    <input type="url" value={service.image} onChange={(e) => updateService(index, 'image', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
                  </div>
                </div>

                {/* Short Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kratek opis (za kartico na glavni strani)</label>
                  <textarea value={service.description} onChange={(e) => updateService(index, 'description', e.target.value)} rows={2}
                    placeholder="Kratek opis, ki se prikaže na kartici storitve..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
                </div>

                {/* Full Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Podroben opis (za podstran storitve)</label>
                  <textarea value={service.full_description || ''} onChange={(e) => updateService(index, 'full_description', e.target.value)} rows={6}
                    placeholder="Napišite podroben opis storitve. Ta tekst se bo prikazal ko obiskovalec klikne na storitev.

Lahko vključite:
- Opis postopka
- Materiale ki jih uporabljate
- Prednosti vaše storitve
- Trajanje del
- Kaj stranka lahko pričakuje"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
                </div>

                {/* Gallery Section */}
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex justify-between items-center mb-3">
                    <label className="block text-sm font-medium text-gray-700">Galerija slik za to storitev</label>
                    <button 
                      onClick={() => addGalleryImage(index)} 
                      className="flex items-center gap-1 bg-blue-500 text-white px-3 py-1.5 rounded-lg hover:bg-blue-600 text-sm"
                    >
                      <Plus size={16} /> Dodaj sliko
                    </button>
                  </div>
                  
                  {(service.gallery_images || []).length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {(service.gallery_images || []).map((img, imgIndex) => (
                        <div key={imgIndex} className="relative group">
                          <img src={img} alt={`${service.title} - ${imgIndex + 1}`} className="w-full h-24 object-cover rounded-lg" />
                          <button
                            onClick={() => removeGalleryImage(index, imgIndex)}
                            className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <XCircle size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm text-center py-4">Ni slik v galeriji. Kliknite "Dodaj sliko" za dodajanje.</p>
                  )}
                  
                  <div className="mt-3">
                    <label className="block text-xs text-gray-500 mb-1">Ali pa vnesite vse URL-je naenkrat (ločeni z vejico):</label>
                    <input 
                      type="text"
                      value={(service.gallery_images || []).join(', ')} 
                      onChange={(e) => updateService(index, 'gallery_images', e.target.value.split(',').map(url => url.trim()).filter(url => url))} 
                      placeholder="https://slika1.jpg, https://slika2.jpg, ..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent text-sm" />
                  </div>
                </div>

                {/* Preview */}
                {service.image && (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span>Glavna slika:</span>
                    <img src={service.image} alt={service.title} className="w-16 h-16 object-cover rounded-lg border-2 border-[#4CAF50]" />
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      
      <div className="pt-6 border-t"><SaveButton onSave={onSave} saving={saving} /></div>
    </div>
  );
};

// Gallery Tab
const GalleryTab = ({ content, onChange, onSave, saving }) => {
  const images = content.gallery_images || [];
  
  const updateImage = (index, field, value) => {
    const updated = [...images];
    updated[index] = { ...updated[index], [field]: value };
    onChange('gallery_images', updated);
  };
  
  const addImage = () => {
    onChange('gallery_images', [...images, { id: Date.now().toString(), url: '', title: 'Nova slika', description: '' }]);
  };
  
  const removeImage = (index) => {
    if (window.confirm('Ali ste prepričani?')) {
      onChange('gallery_images', images.filter((_, i) => i !== index));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-bold text-[#2D4A5E]">Galerija slik</h3>
        <button onClick={addImage} className="flex items-center gap-2 bg-[#4CAF50] text-white px-4 py-2 rounded-lg hover:bg-[#45a049]">
          <Plus size={18} /> Dodaj sliko
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Naslov sekcije</label>
          <input type="text" value={content.gallery_title || ''} onChange={(e) => onChange('gallery_title', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Podnaslov sekcije</label>
          <input type="text" value={content.gallery_subtitle || ''} onChange={(e) => onChange('gallery_subtitle', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {images.map((image, index) => (
          <div key={image.id} className="bg-gray-50 rounded-lg p-4">
            <div className="flex justify-between items-start mb-3">
              <span className="font-medium text-[#2D4A5E]">Slika {index + 1}</span>
              <button onClick={() => removeImage(index)} className="text-red-500 hover:text-red-700"><Trash2 size={18} /></button>
            </div>
            <div className="space-y-3">
              <input type="url" placeholder="URL slike" value={image.url} onChange={(e) => updateImage(index, 'url', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
              <input type="text" placeholder="Naslov" value={image.title} onChange={(e) => updateImage(index, 'title', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
              <input type="text" placeholder="Opis" value={image.description} onChange={(e) => updateImage(index, 'description', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
              {image.url && <img src={image.url} alt={image.title} className="w-full h-32 object-cover rounded-lg" />}
            </div>
          </div>
        ))}
      </div>
      
      <div className="pt-6 border-t"><SaveButton onSave={onSave} saving={saving} /></div>
    </div>
  );
};

// Why Tab
const WhyTab = ({ content, onChange, onSave, saving }) => {
  const items = content.why_items || [];
  const icons = ['Award', 'Palette', 'Clock', 'Shield', 'Heart', 'Users', 'Zap', 'Settings'];
  
  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    onChange('why_items', updated);
  };
  
  const addItem = () => {
    onChange('why_items', [...items, { id: Date.now().toString(), title: 'Nov razlog', description: '', icon: 'Award' }]);
  };
  
  const removeItem = (index) => {
    if (window.confirm('Ali ste prepričani?')) {
      onChange('why_items', items.filter((_, i) => i !== index));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-bold text-[#2D4A5E]">Zakaj izbrati nas</h3>
        <button onClick={addItem} className="flex items-center gap-2 bg-[#4CAF50] text-white px-4 py-2 rounded-lg hover:bg-[#45a049]">
          <Plus size={18} /> Dodaj razlog
        </button>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Naslov sekcije</label>
        <input type="text" value={content.why_title || ''} onChange={(e) => onChange('why_title', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((item, index) => (
          <div key={item.id} className="bg-gray-50 rounded-lg p-4">
            <div className="flex justify-between items-start mb-3">
              <span className="font-medium text-[#2D4A5E]">Razlog {index + 1}</span>
              <button onClick={() => removeItem(index)} className="text-red-500 hover:text-red-700"><Trash2 size={18} /></button>
            </div>
            <div className="space-y-3">
              <input type="text" placeholder="Naslov" value={item.title} onChange={(e) => updateItem(index, 'title', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
              <textarea placeholder="Opis" value={item.description} onChange={(e) => updateItem(index, 'description', e.target.value)} rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
              <select value={item.icon} onChange={(e) => updateItem(index, 'icon', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent">
                {icons.map(icon => <option key={icon} value={icon}>{icon}</option>)}
              </select>
            </div>
          </div>
        ))}
      </div>
      
      <div className="pt-6 border-t"><SaveButton onSave={onSave} saving={saving} /></div>
    </div>
  );
};

// About Tab
const AboutTab = ({ content, onChange, onSave, saving }) => {
  const features = content.about_features || [];
  const icons = ['Award', 'Palette', 'Clock', 'Shield', 'Heart', 'Users', 'Zap', 'Settings', 'CheckCircle', 'Star'];

  const updateFeature = (index, field, value) => {
    const updated = [...features];
    updated[index] = { ...updated[index], [field]: value };
    onChange('about_features', updated);
  };

  const addFeature = () => {
    onChange('about_features', [...features, { title: 'Nova lastnost', description: '', icon: 'Award' }]);
  };

  const removeFeature = (index) => {
    if (window.confirm('Ali ste prepričani?')) {
      onChange('about_features', features.filter((_, i) => i !== index));
    }
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-bold text-[#2D4A5E]">O nas</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Naslov</label>
          <input type="text" value={content.about_title || ''} onChange={(e) => onChange('about_title', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Slika (URL)</label>
          <input type="url" value={content.about_image || ''} onChange={(e) => onChange('about_image', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">Besedilo</label>
          <textarea value={content.about_text || ''} onChange={(e) => onChange('about_text', e.target.value)} rows={6}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
        </div>
      </div>
      
      {content.about_image && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Predogled slike</label>
          <img src={content.about_image} alt="O nas" className="w-full max-w-md h-48 object-cover rounded-lg border" />
        </div>
      )}

      {/* About Features (cards) */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-semibold text-[#2D4A5E]">Lastnosti / Kartice</h4>
          <button onClick={addFeature} className="flex items-center gap-2 bg-[#4CAF50] text-white px-3 py-1.5 rounded-lg hover:bg-[#45a049] text-sm">
            <Plus size={16} /> Dodaj
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {features.map((feature, index) => (
            <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex justify-between items-start mb-3">
                <span className="font-medium text-[#2D4A5E]">{feature.title || 'Lastnost'}</span>
                <button onClick={() => removeFeature(index)} className="text-red-500 hover:text-red-700"><Trash2 size={16} /></button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Naslov</label>
                  <input type="text" value={feature.title || ''} onChange={(e) => updateFeature(index, 'title', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Opis</label>
                  <input type="text" value={feature.description || ''} onChange={(e) => updateFeature(index, 'description', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Ikona</label>
                  <select value={feature.icon || 'Award'} onChange={(e) => updateFeature(index, 'icon', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent text-sm">
                    {icons.map(icon => <option key={icon} value={icon}>{icon}</option>)}
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {features.length === 0 && (
          <p className="text-gray-400 text-sm text-center py-4">Ni lastnosti. Kliknite "Dodaj" za dodajanje.</p>
        )}
      </div>
      
      <div className="pt-6 border-t"><SaveButton onSave={onSave} saving={saving} /></div>
    </div>
  );
};

// Contact Tab
const ContactTab = ({ content, onChange, onSave, saving }) => {
  const phoneNumbers = content.phone_numbers || [{ name: '', number: '' }, { name: '', number: '' }];
  const serviceTypes = content.service_types || [];
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);

  const updatePhoneNumber = (index, field, value) => {
    const updated = [...phoneNumbers];
    if (!updated[index]) updated[index] = { name: '', number: '' };
    updated[index] = { ...updated[index], [field]: value };
    onChange('phone_numbers', updated);
  };

  const updateServiceType = (index, field, value) => {
    const updated = [...serviceTypes];
    updated[index] = { ...updated[index], [field]: value };
    onChange('service_types', updated);
  };

  const addServiceType = () => {
    onChange('service_types', [...serviceTypes, { value: `storitev_${Date.now()}`, label: 'Nova storitev' }]);
  };

  const removeServiceType = (index) => {
    if (window.confirm('Ali ste prepričani?')) {
      onChange('service_types', serviceTypes.filter((_, i) => i !== index));
    }
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-bold text-[#2D4A5E]">Kontaktni podatki</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Ime podjetja</label>
          <input type="text" value={content.company_name || ''} onChange={(e) => onChange('company_name', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Slogan</label>
          <input type="text" value={content.slogan || ''} onChange={(e) => onChange('slogan', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
        </div>
      </div>

      {/* Phone Numbers */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-[#2D4A5E] mb-4">Telefonske številke</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Telefon 1 - Ime</label>
            <input type="text" placeholder="npr. Glavna pisarna" value={phoneNumbers[0]?.name || ''} onChange={(e) => updatePhoneNumber(0, 'name', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
            <label className="block text-sm font-medium text-gray-700">Telefon 1 - Številka</label>
            <input type="text" placeholder="+386 XX XXX XXX" value={phoneNumbers[0]?.number || ''} onChange={(e) => updatePhoneNumber(0, 'number', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Telefon 2 - Ime</label>
            <input type="text" placeholder="npr. Mobilni" value={phoneNumbers[1]?.name || ''} onChange={(e) => updatePhoneNumber(1, 'name', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
            <label className="block text-sm font-medium text-gray-700">Telefon 2 - Številka</label>
            <input type="text" placeholder="+386 XX XXX XXX" value={phoneNumbers[1]?.number || ''} onChange={(e) => updatePhoneNumber(1, 'number', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Email (prikazan)</label>
          <input type="email" value={content.email || ''} onChange={(e) => onChange('email', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Email za obvestila</label>
          <input type="email" value={content.notification_email || ''} onChange={(e) => onChange('notification_email', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Naslov</label>
          <input type="text" value={content.address || ''} onChange={(e) => onChange('address', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Facebook URL</label>
          <input type="url" value={content.facebook_url || ''} onChange={(e) => onChange('facebook_url', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Instagram URL</label>
          <input type="url" value={content.instagram_url || ''} onChange={(e) => onChange('instagram_url', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" placeholder="https://instagram.com/..." />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Google Ocena URL</label>
          <input type="url" value={content.google_review_url || ''} onChange={(e) => onChange('google_review_url', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" placeholder="https://g.page/r/..." />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Delovni dnevi</label>
          <input type="text" value={content.working_hours_weekdays || ''} onChange={(e) => onChange('working_hours_weekdays', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Vikend</label>
          <input type="text" value={content.working_hours_weekend || ''} onChange={(e) => onChange('working_hours_weekend', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">Opis v nogi</label>
          <textarea value={content.footer_description || ''} onChange={(e) => onChange('footer_description', e.target.value)} rows={3}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
        </div>
      </div>

      {/* Email Settings */}
      <div className="bg-blue-50 rounded-lg p-4">
        <h4 className="font-semibold text-[#2D4A5E] mb-4">📧 Nastavitve pošiljanja emailov</h4>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Način pošiljanja</label>
          <select value={content.email_method || 'emailjs'} onChange={(e) => onChange('email_method', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent bg-white">
            <option value="emailjs">EmailJS (priporočeno)</option>
            <option value="smtp">Gmail SMTP</option>
          </select>
        </div>

        {content.email_method === 'emailjs' && (
          <div className="space-y-4 p-4 bg-white rounded-lg">
            <p className="text-sm text-gray-600 mb-4">
              Za nastavitev EmailJS:<br/>
              1. Pojdite na <a href="https://www.emailjs.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">emailjs.com</a> in ustvarite brezplačen račun<br/>
              2. Dodajte Gmail servis (Email Services → Add New Service → Gmail)<br/>
              3. Ustvarite template (Email Templates → Create New Template)<br/>
              4. V template uporabite spremenljivke: {"{{from_name}}"}, {"{{from_email}}"}, {"{{phone}}"}, {"{{service}}"}, {"{{message}}"}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service ID</label>
                <input type="text" value={content.emailjs_service_id || ''} onChange={(e) => onChange('emailjs_service_id', e.target.value)}
                  placeholder="service_xxxxxxx" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Template ID</label>
                <input type="text" value={content.emailjs_template_id || ''} onChange={(e) => onChange('emailjs_template_id', e.target.value)}
                  placeholder="template_xxxxxxx" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Public Key</label>
                <input type="text" value={content.emailjs_public_key || ''} onChange={(e) => onChange('emailjs_public_key', e.target.value)}
                  placeholder="xxxxxxxxxxxxxxx" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
              </div>
            </div>
          </div>
        )}

        {content.email_method === 'smtp' && (
          <div className="space-y-4 p-4 bg-white rounded-lg">
            <p className="text-sm text-gray-600 mb-4">
              Za Gmail SMTP potrebujete App Password:<br/>
              1. Pojdite v Gmail → Settings → Security<br/>
              2. Vklopite 2-Step Verification<br/>
              3. Ustvarite App Password (App Passwords → Generate)
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gmail naslov</label>
                <input type="email" value={content.smtp_email || ''} onChange={(e) => onChange('smtp_email', e.target.value)}
                  placeholder="vas.email@gmail.com" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">App Password</label>
                <div className="relative">
                  <input type={showSmtpPassword ? "text" : "password"} value={content.smtp_password || ''} onChange={(e) => onChange('smtp_password', e.target.value)}
                    placeholder="xxxx xxxx xxxx xxxx" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
                  <button type="button" onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <Eye size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Service Types for Contact Form */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-semibold text-[#2D4A5E]">Vrste storitev (za obrazec povpraševanja)</h4>
          <button onClick={addServiceType} className="flex items-center gap-2 bg-[#4CAF50] text-white px-3 py-1.5 rounded-lg hover:bg-[#45a049] text-sm">
            <Plus size={16} /> Dodaj
          </button>
        </div>
        <div className="space-y-2">
          {serviceTypes.map((type, index) => (
            <div key={index} className="flex items-center gap-2">
              <input type="text" placeholder="Vrednost (npr. les)" value={type.value} onChange={(e) => updateServiceType(index, 'value', e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent text-sm" />
              <input type="text" placeholder="Naziv (npr. Barvanje lesa)" value={type.label} onChange={(e) => updateServiceType(index, 'label', e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent text-sm" />
              <button onClick={() => removeServiceType(index)} className="text-red-500 hover:text-red-700 p-1"><Trash2 size={18} /></button>
            </div>
          ))}
        </div>
      </div>
    
    <div className="pt-6 border-t"><SaveButton onSave={onSave} saving={saving} /></div>
  </div>
  );
};

// Images Tab (Logo and Brand Images)
const ImagesTab = ({ content, onChange, onSave, saving }) => (
  <div className="space-y-6">
    <h3 className="text-lg font-bold text-[#2D4A5E]">Slike in Logotip</h3>
    
    {/* Logo Section */}
    <div className="bg-gray-50 rounded-lg p-4">
      <h4 className="font-semibold text-[#2D4A5E] mb-4">Logotip podjetja</h4>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">URL logotipa</label>
        <input type="url" value={content.logo_url || ''} onChange={(e) => onChange('logo_url', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
      </div>
      
      {content.logo_url && (
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Predogled logotipa</label>
          <div className="flex gap-4">
            <div className="bg-[#2D4A5E] p-4 rounded-lg">
              <img src={content.logo_url} alt="Logotip (temno ozadje)" className="h-16 object-contain" />
            </div>
            <div className="bg-white border p-4 rounded-lg">
              <img src={content.logo_url} alt="Logotip (svetlo ozadje)" className="h-16 object-contain" />
            </div>
          </div>
        </div>
      )}
    </div>

    {/* Hero Image */}
    <div className="bg-gray-50 rounded-lg p-4">
      <h4 className="font-semibold text-[#2D4A5E] mb-4">Glavna slika (Hero)</h4>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">URL slike za ozadje</label>
        <input type="url" value={content.hero_background_image || ''} onChange={(e) => onChange('hero_background_image', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
      </div>
      
      {content.hero_background_image && (
        <div className="mt-4">
          <img src={content.hero_background_image} alt="Hero ozadje" className="w-full max-w-md h-40 object-cover rounded-lg" />
        </div>
      )}
    </div>

    {/* About Image */}
    <div className="bg-gray-50 rounded-lg p-4">
      <h4 className="font-semibold text-[#2D4A5E] mb-4">Slika za sekcijo "O nas"</h4>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">URL slike</label>
        <input type="url" value={content.about_image || ''} onChange={(e) => onChange('about_image', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" />
      </div>
      
      {content.about_image && (
        <div className="mt-4">
          <img src={content.about_image} alt="O nas" className="w-full max-w-md h-40 object-cover rounded-lg" />
        </div>
      )}
    </div>
    
    <div className="bg-blue-50 p-4 rounded-lg">
      <p className="text-sm text-blue-800">
        <strong>Namig:</strong> Za nalaganje slik lahko uporabite storitve kot so Imgur, Google Drive (javna povezava) ali pa naložite sliko na vaš strežnik in vnesete URL.
      </p>
    </div>
    
    <div className="pt-6 border-t"><SaveButton onSave={onSave} saving={saving} /></div>
  </div>
);

// SEO Tab
const SeoTab = ({ content, onChange, onSave, saving }) => (
  <div className="space-y-6">
    <h3 className="text-lg font-bold text-[#2D4A5E]">SEO Nastavitve & Analitika</h3>
    
    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
      <p className="text-sm text-green-800">
        <strong>✅ Implementirano:</strong> Strukturirani podatki (Schema.org), Open Graph, Twitter Cards, meta oznake
      </p>
    </div>

    {/* Google Analytics */}
    <div className="bg-orange-50 rounded-lg p-4">
      <h4 className="font-semibold text-orange-800 mb-3">📊 Google Analytics</h4>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Google Analytics Measurement ID
        </label>
        <input 
          type="text" 
          value={content.google_analytics_id || ''} 
          onChange={(e) => onChange('google_analytics_id', e.target.value)}
          placeholder="G-XXXXXXXXXX"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" 
        />
        <p className="text-xs text-gray-500 mt-2">
          Pridobite ID na <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">analytics.google.com</a> → Admin → Data Streams → vaša stran → Measurement ID
        </p>
      </div>
    </div>
    
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          SEO Naslov (prikazan v Google rezultatih)
          <span className="text-gray-400 ml-2">Priporočeno: 50-60 znakov</span>
        </label>
        <input 
          type="text" 
          value={content.seo_title || ''} 
          onChange={(e) => onChange('seo_title', e.target.value)}
          placeholder="ANZO Barvanje & Čiščenje | Profesionalno barvanje lesa, fasad"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" 
        />
        <p className="text-xs text-gray-500 mt-1">{(content.seo_title || '').length} znakov</p>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          SEO Opis (prikazan v Google rezultatih)
          <span className="text-gray-400 ml-2">Priporočeno: 150-160 znakov</span>
        </label>
        <textarea 
          value={content.seo_description || ''} 
          onChange={(e) => onChange('seo_description', e.target.value)}
          rows={3}
          placeholder="ANZO - Specializirano podjetje za barvanje in obnovo zunanjih površin..."
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" 
        />
        <p className="text-xs text-gray-500 mt-1">{(content.seo_description || '').length} znakov</p>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Ključne besede (ločene z vejico)
        </label>
        <textarea 
          value={content.seo_keywords || ''} 
          onChange={(e) => onChange('seo_keywords', e.target.value)}
          rows={2}
          placeholder="barvanje lesa, barvanje fasad, barvanje železa, obnova fasade..."
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent" 
        />
      </div>
    </div>
    
    <div className="bg-blue-50 rounded-lg p-4">
      <h4 className="font-semibold text-blue-800 mb-2">💡 Nasveti za boljšo pozicijo v Google</h4>
      <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
        <li>Registrirajte podjetje na <a href="https://business.google.com" target="_blank" rel="noopener noreferrer" className="underline">Google My Business</a></li>
        <li>Naslov naj vsebuje glavne ključne besede</li>
        <li>Redno dodajajte nove slike v galerijo</li>
        <li>Delite stran na družbenih omrežjih</li>
        <li>Pridobite povezave z drugih spletnih strani</li>
      </ul>
    </div>
    
    <div className="bg-gray-50 rounded-lg p-4">
      <h4 className="font-semibold text-[#2D4A5E] mb-2">📋 Predogled v Google iskalniku</h4>
      <div className="bg-white rounded-lg p-4 border">
        <p className="text-blue-600 text-lg hover:underline cursor-pointer">
          {content.seo_title || 'ANZO Barvanje & Čiščenje | Profesionalno barvanje'}
        </p>
        <p className="text-green-700 text-sm">https://anzo-barvanje.si</p>
        <p className="text-gray-600 text-sm mt-1">
          {(content.seo_description || 'Specializirano podjetje za barvanje in obnovo zunanjih površin...').substring(0, 160)}
          {(content.seo_description || '').length > 160 ? '...' : ''}
        </p>
      </div>
    </div>
    
    <div className="pt-6 border-t"><SaveButton onSave={onSave} saving={saving} /></div>
  </div>
);

// Security Tab (Password)
const SecurityTab = ({ content, onChange, onSave, saving }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const handlePasswordChange = async () => {
    setPasswordError('');
    setPasswordSuccess(false);

    if (newPassword.length < 4) {
      setPasswordError('Geslo mora imeti vsaj 4 znake.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Gesli se ne ujemata.');
      return;
    }

    // Update password in content
    onChange('admin_password', newPassword);
    
    // Save immediately
    try {
      await onSave();
      setPasswordSuccess(true);
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (err) {
      setPasswordError('Napaka pri shranjevanju gesla.');
    }
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-bold text-[#2D4A5E]">Varnostne nastavitve</h3>
      
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-sm text-yellow-800">
          <strong>Opozorilo:</strong> Po spremembi gesla si ga obvezno zapomnite! Če pozabite geslo, ga ne bo mogoče obnoviti brez pomoči razvijalca.
        </p>
      </div>

      <div className="max-w-md space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Trenutno geslo</label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={content.admin_password || 'anzo2024'}
              readOnly
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <Eye size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>

        <hr className="my-6" />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Novo geslo</label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Vnesite novo geslo"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Potrdite novo geslo</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Ponovno vnesite novo geslo"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent"
          />
        </div>

        {passwordError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {passwordError}
          </div>
        )}

        {passwordSuccess && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
            <CheckCircle size={18} />
            Geslo uspešno spremenjeno!
          </div>
        )}

        <button
          onClick={handlePasswordChange}
          disabled={saving || !newPassword || !confirmPassword}
          className="flex items-center gap-2 bg-[#4CAF50] text-white px-6 py-3 rounded-lg hover:bg-[#45a049] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Lock size={20} />}
          Spremeni geslo
        </button>
      </div>
    </div>
  );
};

// Landing Page Tab (QR/NFC)
const LandingTab = ({ content, onChange, onSave, saving }) => {
  const [stats, setStats] = useState({ total_qr: 0, total_nfc: 0, total: 0, recent_visits: [] });
  const [loadingStats, setLoadingStats] = useState(true);
  const [resetting, setResetting] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const FRONTEND_URL = window.location.origin;

  const fetchStats = async () => {
    setLoadingStats(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/landing-stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [BACKEND_URL]);

  const resetStats = async (source = null) => {
    const confirmMsg = source 
      ? `Ali ste prepričani, da želite ponastaviti števec za ${source.toUpperCase()}?` 
      : 'Ali ste prepričani, da želite ponastaviti VSE števce?';
    
    if (!window.confirm(confirmMsg)) return;
    
    setResetting(true);
    try {
      const url = source 
        ? `${BACKEND_URL}/api/landing-stats/reset?source=${source}` 
        : `${BACKEND_URL}/api/landing-stats/reset`;
      await axios.delete(url);
      await fetchStats();
    } catch (error) {
      console.error('Error resetting stats:', error);
      alert('Napaka pri ponastavitvi števca.');
    } finally {
      setResetting(false);
    }
  };

  const landingPage = content.landing_page || {};

  const updateLandingPage = (field, value) => {
    onChange('landing_page', { ...landingPage, [field]: value });
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-bold text-[#2D4A5E]">QR koda / NFC nastavitve</h3>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white relative">
          <button
            onClick={() => resetStats('qr')}
            disabled={resetting}
            className="absolute top-2 right-2 p-1.5 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
            title="Ponastavi QR števec"
          >
            <RotateCcw size={16} />
          </button>
          <div className="flex items-center gap-3 mb-2">
            <QrCode size={24} />
            <span className="text-sm opacity-80">QR koda obiski</span>
          </div>
          <p className="text-4xl font-bold">{loadingStats ? '...' : stats.total_qr}</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white relative">
          <button
            onClick={() => resetStats('nfc')}
            disabled={resetting}
            className="absolute top-2 right-2 p-1.5 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
            title="Ponastavi NFC števec"
          >
            <RotateCcw size={16} />
          </button>
          <div className="flex items-center gap-3 mb-2">
            <Smartphone size={24} />
            <span className="text-sm opacity-80">NFC obiski</span>
          </div>
          <p className="text-4xl font-bold">{loadingStats ? '...' : stats.total_nfc}</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white relative">
          <button
            onClick={() => resetStats(null)}
            disabled={resetting}
            className="absolute top-2 right-2 p-1.5 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
            title="Ponastavi vse števce"
          >
            <RotateCcw size={16} />
          </button>
          <div className="flex items-center gap-3 mb-2">
            <BarChart3 size={24} />
            <span className="text-sm opacity-80">Skupaj</span>
          </div>
          <p className="text-4xl font-bold">{loadingStats ? '...' : stats.total}</p>
        </div>
      </div>

      {/* Reset Info */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
        <p className="text-sm text-yellow-800">
          <strong>Namig:</strong> Kliknite ikono <RotateCcw size={14} className="inline" /> na kartici za ponastavitev posameznega ali vseh števcev na 0.
        </p>
      </div>

      {/* URLs */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-[#2D4A5E] mb-4">Povezave za QR kodo in NFC vizitko</h4>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-white border rounded-lg px-4 py-2 font-mono text-sm">
              {FRONTEND_URL}/koda
            </div>
            <button
              onClick={() => navigator.clipboard.writeText(`${FRONTEND_URL}/koda`)}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 text-sm"
            >
              Kopiraj QR
            </button>
            <a
              href={`${FRONTEND_URL}/koda`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 text-sm"
            >
              <ExternalLink size={16} />
            </a>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-white border rounded-lg px-4 py-2 font-mono text-sm">
              {FRONTEND_URL}/vizitka
            </div>
            <button
              onClick={() => navigator.clipboard.writeText(`${FRONTEND_URL}/vizitka`)}
              className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 text-sm"
            >
              Kopiraj NFC
            </button>
            <a
              href={`${FRONTEND_URL}/vizitka`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 text-sm"
            >
              <ExternalLink size={16} />
            </a>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-3">
          Uporabite zgornjo povezavo za ustvarjanje QR kode (npr. na qr-code-generator.com) ali za programiranje NFC oznake.
        </p>
      </div>

      {/* Landing Page Settings */}
      <div className="bg-white border rounded-lg p-4">
        <h4 className="font-semibold text-[#2D4A5E] mb-4">Nastavitve pristajajoče strani</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Naslov</label>
            <input
              type="text"
              value={landingPage.title || 'Dobrodošli!'}
              onChange={(e) => updateLandingPage('title', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Podnaslov</label>
            <input
              type="text"
              value={landingPage.subtitle || 'Hvala, da ste nas obiskali'}
              onChange={(e) => updateLandingPage('subtitle', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Opis</label>
            <textarea
              value={landingPage.description || ''}
              onChange={(e) => updateLandingPage('description', e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Gumb - besedilo</label>
            <input
              type="text"
              value={landingPage.cta_text || 'Obiščite našo stran'}
              onChange={(e) => updateLandingPage('cta_text', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Slika ozadja (URL)</label>
            <input
              type="url"
              value={landingPage.background_image || ''}
              onChange={(e) => updateLandingPage('background_image', e.target.value)}
              placeholder="https://..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={landingPage.show_services !== false}
                onChange={(e) => updateLandingPage('show_services', e.target.checked)}
                className="w-5 h-5 rounded border-gray-300 text-[#4CAF50] focus:ring-[#4CAF50]"
              />
              <span className="text-sm text-gray-700">Prikaži storitve</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={landingPage.show_contact !== false}
                onChange={(e) => updateLandingPage('show_contact', e.target.checked)}
                className="w-5 h-5 rounded border-gray-300 text-[#4CAF50] focus:ring-[#4CAF50]"
              />
              <span className="text-sm text-gray-700">Prikaži kontakt</span>
            </label>
          </div>
        </div>
      </div>

      {/* Recent Visits */}
      {stats.recent_visits?.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold text-[#2D4A5E] mb-4">Zadnji obiski</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 font-medium text-gray-500">Vir</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-500">Datum</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_visits.slice(0, 10).map((visit, index) => (
                  <tr key={index} className="border-b">
                    <td className="py-2 px-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        visit.source === 'qr' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                      }`}>
                        {visit.source === 'qr' ? <QrCode size={12} /> : <Smartphone size={12} />}
                        {visit.source.toUpperCase()}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-gray-600">
                      {new Date(visit.timestamp).toLocaleString('sl-SI')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="pt-6 border-t"><SaveButton onSave={onSave} saving={saving} /></div>
    </div>
  );
};

export default AdminPanel;
